import{ useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import './AttachmentPreviewModal.css';
import { formatFileSize, getFileType } from '../../../../../utils/fileUtils';
import useFileDownload from '../../../../../hooks/useFileDownload';
import MessageReactions from './MessageReactions';
import DownloadProgressCircle from '../../../../Common/DownloadProgressCircle';
import SmartEmojiPicker from './SmartEmojiPicker';
import logger from '../../../../../utils/logger';
import ReactionDetailsModal from './ReactionDetailsModal';

const AttachmentPreviewModal = ({
  url,
  filename,
  msgtype,
  fileSize,
  mimeType,
  senderName,
  thumbnailUrl,
  message,
  onReplyMessage,
  onFocusMessage,  
  onToggleReaction,
  displayReactions,
  onClose,
}) => {  
  const [mediaSrc, setMediaSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const { downloadFileToDisplay, downloadFile } = useFileDownload();
  const [isReactionDetailsOpen, setReactionDetailsOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);  
  const reactions = displayReactions || message?._reactions;
  const roomId = message?.getRoomId();

  const handleDownloadClick = useCallback(() => {
    downloadFile({ url, filename, mimeType });
  }, [downloadFile, url, filename, mimeType]);

  const handleReplyClick = useCallback(() => {
    onClose();
    onReplyMessage(message, senderName, filename, msgtype, filename, thumbnailUrl, fileSize);
  }, [onReplyMessage, onClose, message, senderName, filename, msgtype, thumbnailUrl, fileSize]);

  const handleFocusClick = useCallback(() => {
    onClose();
    onFocusMessage(message);
  }, [onFocusMessage, onClose, message]);

  const handleReactClick = (e) => {
    e.stopPropagation();
    setShowEmojiPicker(prev => !prev);
  };

  // Close emoji picker when clicking outside of it
  useEffect(() => {
    if (!showEmojiPicker) return;

    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const blobUrlRef = { current: null };

    const loadMedia = async () => {
      if (!url || !downloadFileToDisplay) {
        setError('Preview not available');
        setIsLoading(false);
        return;
      }

      try {
        const blobUrl = await downloadFileToDisplay({
          url,
          roomId, // Pass roomId to the download function for caching
          onProgress: (p) => setProgress(p.percent),
          signal: controller.signal, // Pass the signal to the download function
        });

        blobUrlRef.current = blobUrl; // Store for cleanup

        if (isMounted) {
          setMediaSrc(blobUrl);
          setIsLoading(false);
        } else {
          URL.revokeObjectURL(blobUrl);
        }
      } catch (e) {
        if (isMounted) {
          if (e.name === 'AbortError') {
            logger.debug('Media download cancelled on modal close.');
            return;
          }
          logger.error('Error loading media for modal:', e);
          setError('Failed to load preview');
          setIsLoading(false);
        }
      }
    };

    loadMedia();

    return () => {
      isMounted = false;
      // Abort the fetch request when the component unmounts (modal closes)
      controller.abort();
      // Revoke the blob URL on cleanup to prevent memory leaks
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, [url, filename, mimeType, downloadFileToDisplay, roomId]);

  // Render the main media content (image, video, or fallback)
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="attachment-modal-loading-content">
          <DownloadProgressCircle progress={progress} size={60} />
          <span className="attachment-modal-loading-text">Loading...</span>
        </div>
      );
    }
    if (error || !mediaSrc) {
      return <div className="attachment-modal-fallback-content"><span>{error || 'Preview not available'}</span></div>;
    }

    if (msgtype === 'm.image') {
      return <img src={mediaSrc} alt={filename} className="attachment-modal-media" />;
    } else if (msgtype === 'm.video') {
      return (
        <video controls autoPlay src={mediaSrc} className="attachment-modal-media">
          Your browser does not support the video tag.
        </video>
      );
    }

    // Fallback for audio, files, etc.
    return (
      <div className="attachment-modal-fallback-content">
        <span>Preview not available</span>
      </div>
    );
  };

  // Render the details block below the media
  const renderDetails = () => {
    const fileType = getFileType(mimeType, filename);
    const formattedSize = formatFileSize(fileSize);

    return (
      <div className="attachment-modal-details">
        <div className="attachment-modal-file-info">
          {filename}
        </div>
        <div className="attachment-modal-file-info">
          {fileType} {formattedSize && `(${formattedSize})`}
        </div>
        {mediaSrc && (
          <a
            href={mediaSrc}
            target="_blank"
            rel="noopener noreferrer"
            className="attachment-modal-view-btn"
          >View Full Screen</a>
        )}
      </div>
    );
  };

  // Render the modal using a portal to ensure it's always on top
  return createPortal(
    <div className="attachment-modal-dark-wrapper" onClick={(e) => e.stopPropagation()}>
      <div className="attachment-modal-overlay">
        {/* UI controls are now direct children of the overlay for screen-relative positioning */}
        <button className="attachment-modal-close-btn" onClick={onClose} aria-label="Close preview">
          <i className="fa fa-times"></i>
        </button>
        <div className="attachment-modal-actions">
          <button className="attachment-modal-action-btn" title="Download" onClick={handleDownloadClick}>
            <i className="fa fa-cloud-download"></i>
          </button>
          <button className="attachment-modal-action-btn" title="Reply" onClick={handleReplyClick}>
            <i className="fa fa-reply"></i>
          </button>
          <button className="attachment-modal-action-btn" title="Focus" onClick={handleFocusClick}>
            <i className="fa fa-commenting"></i>
          </button>
          <button className="attachment-modal-action-btn" title="React" onClick={handleReactClick}>
            <img src='/images/svg/emoji-react.svg' alt='React' className="attachment-modal-react-icon" />
          </button>
        </div>
        <div className="attachment-modal-content-wrapper" onClick={(e) => e.stopPropagation()}>
          <div className="attachment-modal-content">
            <div className="attachment-modal-media-wrapper">
              {renderContent()}
              {reactions && reactions.size > 0 && (
                <MessageReactions
                  reactions={reactions}
                  className="in-modal"
                  onReactionDetailsOpen={() => setReactionDetailsOpen(true)}
                />
              )}
            </div>
            {renderDetails()}
          </div>
        </div>
      </div>
      {showEmojiPicker && (
        <div
          ref={emojiPickerRef}
          className="attachment-modal-emoji-picker-container"
          onClick={(e) => e.stopPropagation()}
        >
          <SmartEmojiPicker
            messageId={message.getId()}
            reactions={reactions}
            onToggleReaction={onToggleReaction}
            onClose={() => setShowEmojiPicker(false)}
          />
        </div>
      )}
      {isReactionDetailsOpen && (
        <ReactionDetailsModal
          reactions={reactions}
          onClose={() => setReactionDetailsOpen(false)}
        />
      )}
    </div>,
    document.body,
  );
};

AttachmentPreviewModal.propTypes = {
  url: PropTypes.string, // The original mxc:// URL
  filename: PropTypes.string.isRequired,
  msgtype: PropTypes.string.isRequired,
  fileSize: PropTypes.number,
  mimeType: PropTypes.string,
  senderName: PropTypes.string.isRequired,
  thumbnailUrl: PropTypes.string,
  message: PropTypes.object.isRequired, // Matrix event object
  displayReactions: PropTypes.instanceOf(Map),
  onReplyMessage: PropTypes.func.isRequired,
  onFocusMessage: PropTypes.func.isRequired,
  onToggleReaction: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AttachmentPreviewModal;